import { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { APIHOST } from '../../../common';
import { Conference } from '../../../types/conference';
import { useApp } from '../../../components/AppContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { getToken } from '../../../components/auth/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface AvailabilityReportProps {
  conferences: Conference[];
}

export default function AvailabilityReport({ conferences }: AvailabilityReportProps) {
  const [selectedConference, setSelectedConference] = useState('');
  const [reportData, setReportData] = useState<any>(null);
  const { setSnackbarInput } = useApp();

  const handleConferenceChange = (event: SelectChangeEvent) => {
    setSelectedConference(event.target.value);
    fetch(`${APIHOST}/api/conferences/${event.target.value}/availability`, {
        headers: {
            'Authorization': `Bearer ${getToken()}`
        }
    })
      .then(response => response.json())
      .then(data => {
        setReportData(data);
        setSnackbarInput({message: 'Conference availability data fetched successfully.'});
      })
      .catch(error => {
        console.error('Error fetching conference availability:', error);
        setSnackbarInput({message: 'Error fetching conference availability.', severity: 'error'});
      });
  };

  // Process report data for Chart.js - one chart per hotel
  const getHotelsFromData = () => {
    if (!reportData) return [];
    return Object.entries(reportData).map(([hotelId, hotel]: [string, any]) => ({
      id: hotelId,
      data: hotel
    }));
  };

  const processHotelChartData = (hotelId: string, hotelData: any) => {
    // Extract all unique dates from all rooms in this hotel
    const allDates = new Set<string>();
    
    Object.values(hotelData.rooms).forEach((room: any) => {
      Object.keys(room.availability).forEach(date => allDates.add(date));
    });

    const sortedDates = Array.from(allDates).sort();

    // Create datasets for each room in this hotel
    const datasets: any[] = [];
    const reservedColors = [
      '#FF5722', '#9C27B0', '#607D8B', '#E91E63', '#673AB7'
    ];
    const availableColor = '#333333'; // Dark grey for all available

    let colorIndex = 0;

    Object.entries(hotelData.rooms).forEach(([roomId, room]: [string, any]) => {
      const reservedColor = reservedColors[colorIndex % reservedColors.length];
      colorIndex++;

      // Calculate percentage data based on allocated rooms
      const reservedData = sortedDates.map(date => {
        const dayData = room.availability[date];
        if (!dayData || dayData.allocated === 0) return 0;
        return dayData.reserved;
      });

      const availableData = sortedDates.map(date => {
        const dayData = room.availability[date];
        if (!dayData || dayData.allocated === 0) return 0;
        return dayData.available;
      });

      // Push reserved first (bottom), then available (top)
      datasets.push({
        label: `${room.name || `Room ${roomId}`} (Reserved)`,
        data: reservedData,
        backgroundColor: reservedColor,
        stack: `room-${roomId}`,
      });

      datasets.push({
        label: `${room.name || `Room ${roomId}`} (Available)`,
        data: availableData,
        backgroundColor: availableColor,
        stack: `room-${roomId}`,
      });
    });

    return {
      labels: sortedDates,
      datasets: datasets,
    };
  };

  const getChartOptions = (hotelName: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          title: function(tooltipItems: any) {
            const date = tooltipItems[0].label.substring(5); // Show MM-DD only
            return `${date.substring(5)}`; // Show only DD
          },
          beforeLabel: function(context: any) {
            // Extract room info from the dataset label
            const label = context.dataset.label;
            const roomName = label.replace(/ \((Reserved|Available)\)$/, '');
            const stackId = context.dataset.stack;
            
            // Find all datasets in the same stack
            const chart = context.chart;
            const datasets = chart.data.datasets;
            const stackDatasets = datasets.filter((ds: any) => ds.stack === stackId);
            
            if (stackDatasets.length >= 2) {
              const reservedDataset = stackDatasets.find((ds: any) => ds.label.includes('(Reserved)'));
              const availableDataset = stackDatasets.find((ds: any) => ds.label.includes('(Available)'));
              
              const reservedValue = reservedDataset ? reservedDataset.data[context.dataIndex] : 0;
              const availableValue = availableDataset ? availableDataset.data[context.dataIndex] : 0;
              const total = reservedValue + availableValue;
              
              return [
                `${roomName}`,
                `----------`,
                `Reserved: ${reservedValue}`,
                `Available: ${availableValue}`,
                `Allocated: ${total}`
              ];
            }
            
            return `${roomName}: ${context.parsed.y}`;
          },
          label: function() {
            return ''; // We're showing all info in beforeLabel
          }
        }
      },
      legend: {
        position: 'top' as const,
        labels: {
          filter: function(legendItem: any) {
            return !legendItem.text.includes('(Available)') && !legendItem.text.includes('70% Target');
          }
        }
      },
      title: {
        display: true,
        text: `${hotelName} - Room Availability by Date`,
      },
      datalabels: {
        color: 'white',
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Allocated Rooms',
        },
        ticks: {
          callback: function(value: any) {
            return value;
          }
        }
      },
    },
  });

  return (
    <>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="conference-select-label">Conference</InputLabel>
        <Select
          labelId="conference-select-label"
          id="conference-select"
          value={selectedConference}
          label="Conference"
          onChange={handleConferenceChange}
        >
          {conferences.map((conference) => (
            <MenuItem key={conference.documentId} value={conference.documentId}>
              {conference.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {reportData && getHotelsFromData().length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" component="h3" sx={{ mb: 3 }}>
            Room Availability Charts by Hotel
          </Typography>
          
          {getHotelsFromData().map(({ id: hotelId, data: hotelData }) => (
            <Paper key={hotelId} sx={{ mb: 4, p: 3 }}>
              <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
                {hotelData.name}
              </Typography>
              <Box sx={{ height: '400px' }}>
                <Bar 
                  data={processHotelChartData(hotelId, hotelData)} 
                  options={{
                    ...getChartOptions(hotelData.name),
                    interaction: {
                      intersect: false,
                    },
                  }}
                  plugins={[ChartDataLabels]} 
                />
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </>
  );
}